import { Box } from '@chakra-ui/react';
import { useReducedMotion } from 'framer-motion';
import type { CSSProperties } from 'react';

/**
 * The companion's face: an iridescent orb with eyes. Decoration, never
 * information: every state it shows is also written in words next to it, so the
 * face is `aria-hidden` and a reader who never sees it misses nothing.
 *
 * Ported from the design canvas. The orb carries its own colours, so it reads
 * the same on the dark top bar and on a white card. The keyframes live in
 * `index.css` under the `companion-face-*` prefix, except the swirl's rotation,
 * which is Chakra's own global `spin`. The global reduced-motion reset already neutralises them, and
 * `useReducedMotion` drops the animation properties entirely so a still face
 * is truly still rather than a one-frame animation.
 */

export type CompanionFaceMood = 'doze' | 'look' | 'alert' | 'happy';

interface CompanionFaceProps {
  mood: CompanionFaceMood;
  /** Diameter in pixels. Eye metrics scale with it, as in the canvas. */
  size?: number;
}

const ORB_BASE = '#A98BFF';
const SWIRL_GRADIENT =
  'conic-gradient(from 20deg, #9B7BFF, #F062D6 20%, #FF9A6B 36%, #34D399 54%, #3FA9FF 72%, #9B7BFF)';
const SHINE_GRADIENT =
  'radial-gradient(circle at 32% 26%, rgba(255,255,255,.6) 0%, rgba(255,255,255,.12) 24%, rgba(255,255,255,0) 46%)';
const RING_COLOR = '#B9A7FF';
const CHEEK_COLOR = '#FF6FA8';
const EYE_COLOR = '#171715';
const RIM_GRADIENT_SMALL =
  'radial-gradient(circle at 50% 120%, rgba(76,50,160,.22) 0%, rgba(76,50,160,0) 55%)';
const RIM_GRADIENT =
  'radial-gradient(circle at 50% 118%, rgba(76,50,160,.34) 0%, rgba(76,50,160,.08) 38%, rgba(76,50,160,0) 60%)';
/** Below this size the glint is lost in the eye, so it is not drawn at all. */
const GLINT_MIN_SIZE = 30;

const SHINE_STYLE: CSSProperties = {
  position: 'absolute',
  inset: 0,
  borderRadius: 'inherit',
  background: SHINE_GRADIENT,
};

/**
 * Each mood's beat. The orb, the rings, the eyes and the cheeks of one mood
 * move on the same loop, so they share its length.
 */
const ALERT_BEAT = '3.2s';
const HAPPY_BEAT = '2.4s';
const LOOK_BEAT = '4s';

const SURPRISE = `companion-face-surprise ${ALERT_BEAT} cubic-bezier(.34,1.4,.64,1) infinite`;
const OPEN_EYE_ANIMATIONS: Record<'alert' | 'look', readonly [string, string]> = {
  alert: [SURPRISE, SURPRISE],
  look: [
    `companion-face-squint-left ${LOOK_BEAT} ease-in-out infinite`,
    `companion-face-squint-right ${LOOK_BEAT} ease-in-out infinite`,
  ],
};

/** Slower when dozing, quicker when it has something to say. */
const TEMPO: Record<CompanionFaceMood, number> = { doze: 1.9, look: 0.45, alert: 0.85, happy: 1 };

const ORB_MOOD_ANIMATION: Record<CompanionFaceMood, string | null> = {
  doze: 'companion-face-breathe 4.5s ease-in-out infinite',
  look: null,
  alert: `companion-face-pop ${ALERT_BEAT} ease-in-out infinite`,
  happy: `companion-face-hop ${HAPPY_BEAT} ease-in-out infinite`,
};

export const CompanionFace = ({ mood, size = 48 }: CompanionFaceProps) => {
  const reducedMotion = useReducedMotion();
  const animated = (animation: string | null): string | undefined =>
    reducedMotion || !animation ? undefined : animation;

  const unit = size / 48;
  // Below 32 px fractional pixels blur the eyes, so they snap to whole ones.
  const small = size < 32;
  const snap = (value: number) => (small ? Math.round(value) : value);
  const eyeWidth = snap(Math.max(3, 5.5 * unit));
  const eyeHeight = snap(Math.max(5, 9 * unit));
  const gap = snap(Math.max(4, 8 * unit));
  const stroke = Math.max(1.5, 2.2 * unit);
  const duration = (seconds: number) => `${(seconds * TEMPO[mood]).toFixed(2)}s`;

  const blob = (
    color: string,
    width: string,
    keyframes: string,
    seconds: number,
    delay: string
  ): CSSProperties => ({
    position: 'absolute',
    left: '15%',
    top: '15%',
    width,
    height: width,
    borderRadius: '50%',
    background: `radial-gradient(circle, ${color} 0%, ${color}CC 30%, ${color}00 70%)`,
    filter: `blur(${Math.max(1, 3 * unit)}px)`,
    animation: animated(
      `${keyframes} ${duration(seconds)} cubic-bezier(.45,.05,.55,.95) ${delay} infinite`
    ),
  });

  const ring = (delay: string): CSSProperties => ({
    position: 'absolute',
    inset: 0,
    borderRadius: '9999px',
    border: `${Math.max(1, 1.5 * unit)}px solid ${RING_COLOR}`,
    pointerEvents: 'none',
    animation: animated(
      `companion-face-ring ${ALERT_BEAT} ${delay} cubic-bezier(.2,.6,.3,1) infinite`
    ),
  });

  const eye = (animation: string, transformOrigin = '50% 60%'): CSSProperties => ({
    position: 'relative',
    overflow: 'hidden',
    display: 'block',
    width: `${eyeWidth}px`,
    height: `${eyeHeight}px`,
    background: EYE_COLOR,
    borderRadius: '9999px',
    transformOrigin,
    animation: animated(animation),
  });

  const glint: CSSProperties = {
    position: 'absolute',
    top: '14%',
    right: '12%',
    width: '42%',
    height: '28%',
    borderRadius: '9999px',
    background: '#FFFFFF',
    animation: animated('companion-face-glint 2.6s ease-in-out infinite'),
  };

  const cheek = (side: 'left' | 'right'): CSSProperties => ({
    position: 'absolute',
    top: '58%',
    [side]: '20%',
    width: `${eyeWidth * 1.7}px`,
    height: `${eyeWidth * 0.95}px`,
    borderRadius: '9999px',
    background: CHEEK_COLOR,
    filter: `blur(${Math.max(0.6, 1.4 * unit)}px)`,
    zIndex: 2,
    animation: animated(`companion-face-cheek ${HAPPY_BEAT} ease-in-out infinite`),
  });

  const happyEye: CSSProperties = {
    display: 'block',
    boxSizing: 'border-box',
    width: `${eyeWidth * 1.8}px`,
    height: `${eyeWidth * 1.05}px`,
    marginTop: `${eyeHeight * 0.15}px`,
    border: `${stroke}px solid ${EYE_COLOR}`,
    borderBottomWidth: 0,
    borderBottomColor: 'transparent',
    borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
    animation: animated(`companion-face-smile ${HAPPY_BEAT} ease-in-out infinite`),
  };

  const orbMoodAnimation = ORB_MOOD_ANIMATION[mood];
  const float = `companion-face-float ${duration(6.1)} ease-in-out infinite`;
  const eyesAnimation: Record<CompanionFaceMood, string | null> = {
    look: `companion-face-look ${LOOK_BEAT} ease-in-out infinite`,
    doze: null,
    alert: float,
    happy: float,
  };
  const showGlint = size >= GLINT_MIN_SIZE;

  return (
    <Box
      aria-hidden="true"
      data-testid="companion-face"
      data-mood={mood}
      position="relative"
      flex="none"
      display="block"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        ['--companion-face-look' as string]: `${Math.max(1, 2.5 * unit)}px`,
      }}
    >
      {mood === 'alert' ? (
        <>
          <span data-testid="companion-face-ring" style={ring('0s')} />
          <span data-testid="companion-face-ring" style={ring('1.1s')} />
        </>
      ) : null}

      <span
        data-testid="companion-face-orb"
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          borderRadius: '50%',
          background: ORB_BASE,
          isolation: 'isolate',
          filter: mood === 'doze' ? 'saturate(.6)' : 'none',
          transition: 'filter .4s',
          animation: animated(
            `companion-face-morph ${duration(9.3)} ease-in-out infinite${orbMoodAnimation ? `, ${orbMoodAnimation}` : ''}`
          ),
        }}
      >
        <span
          style={{
            position: 'absolute',
            inset: '-30%',
            borderRadius: '50%',
            background: SWIRL_GRADIENT,
            filter: `blur(${Math.max(1.5, 5 * unit)}px) saturate(1.15)`,
            animation: animated(`spin ${duration(14)} linear infinite`),
          }}
        />
        <span style={blob('#F062D6', '62%', 'companion-face-drift-a', 7.3, '0s')} />
        <span style={blob('#3FA9FF', '66%', 'companion-face-drift-b', 9.7, '-2.1s')} />
        <span style={blob('#34D399', '54%', 'companion-face-drift-c', 11.9, '-4.3s')} />
        <span style={SHINE_STYLE} />
        {/* The rim light is part of the drawing, not an elevation shadow. */}
        <span
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            background: small ? RIM_GRADIENT_SMALL : RIM_GRADIENT,
            boxShadow: `inset 0 0 0 ${small ? 1 : Math.max(1, 1.2 * unit)}px rgba(255,255,255,${small ? '.32' : '.22'}), inset 0 ${(-2 * unit).toFixed(1)}px ${(5 * unit).toFixed(1)}px rgba(255,255,255,.18)`,
          }}
        />

        {mood === 'happy' ? (
          <>
            <span data-testid="companion-face-cheek" style={cheek('left')} />
            <span data-testid="companion-face-cheek" style={cheek('right')} />
          </>
        ) : null}

        <span
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '44%',
            marginTop: `${-eyeHeight / 2}px`,
            height: `${eyeHeight}px`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: `${gap}px`,
            zIndex: 2,
            animation: animated(eyesAnimation[mood]),
          }}
        >
          {mood === 'alert' || mood === 'look'
            ? OPEN_EYE_ANIMATIONS[mood].map((animation, index) => (
                <span key={index} data-testid="companion-face-eye" style={eye(animation)}>
                  {showGlint ? <span data-testid="companion-face-glint" style={glint} /> : null}
                </span>
              ))
            : null}

          {mood === 'doze'
            ? ['0s', '.25s'].map((delay) => (
                <span
                  key={delay}
                  data-testid="companion-face-doze-eye"
                  style={eye(`companion-face-peek 7s ${delay} ease-in-out infinite`, '50% 75%')}
                />
              ))
            : null}

          {mood === 'happy'
            ? [0, 1].map((index) => (
                <span key={index} data-testid="companion-face-happy-eye" style={happyEye} />
              ))
            : null}
        </span>
      </span>
    </Box>
  );
};
